import { FiberManualRecord } from "@mui/icons-material";
import { Box, Button, Card, Chip, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { gatewayPhone } from "../gateways/gatewayPhone";
import { setPlanSelected } from "../redux/features/planStateSlice";
import { BusinessContact } from "./businessContact";
import { colors, alphaVar } from "./ui/colors";

// Icons unificados
const Icons = {
  Star: ({ color = "currentColor" }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <polygon points="12 2 15.09 10.26 24 10.35 17.77 16.01 20.16 24.02 12 18.77 3.84 24.02 6.23 16.01 0 10.35 8.91 10.26 12 2" />
    </svg>
  ),
  Check: ({ color = "currentColor" }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  ArrowRight: ({ color = "currentColor" }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  ),
  CheckCircle: ({ color = "currentColor" }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
};

const formatPrice = (amount) => {
  const num = parseFloat(amount ?? 0);
  return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
};

export const Plan = ({
  subscriptionId,
  title,
  description,
  benefits,
  price,
  type,
  frequency,
  planId,
  billingType,
  isCustom,
  isPopular,
  trialDetails,
  isComingSoon,
  comingSoonLabel,
  isCurrentPlan,
  isSelected,
  onPreview,
}) => {
  const [contactUs, setContactUs] = useState(false);
  const dispatch = useDispatch();

  const bannerColor = colors.orange.base;
  const currentPlanColor = "#2E7D32";
  const textPrimary = colors.gray.base;
  const isFreePlan = title?.toLowerCase() === "free";

  const accentColor = isCurrentPlan ? currentPlanColor : bannerColor;

  const [usagePricing, setUsagePricing] = useState([]);
  const [loadingUsagePricing, setLoadingUsagePricing] = useState(false);

  useEffect(() => {
    if (billingType === "PAY_AS_YOU_GO") {
      let isMounted = true;
      setLoadingUsagePricing(true);
      gatewayPhone
        .get("/usage/pricing")
        .then((res) => {
          if (!isMounted) return;
          const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
          const filtered = data.filter((item) => (item?.channel === "call" || item?.channel === "sms") && item?.isActive !== false);
          setUsagePricing(filtered);
        })
        .catch((err) => {
          console.error("Failed to fetch usage pricing rates:", err);
        })
        .finally(() => {
          if (isMounted) setLoadingUsagePricing(false);
        });
      return () => {
        isMounted = false;
      };
    }
  }, [billingType]);

  const handleChoosePlan = () => {
    if (isCurrentPlan) return;
    if (isCustom) {
      setContactUs(true);
    } else {
      dispatch(
        setPlanSelected({
          planSelected: {
            subscriptionId,
            name: title,
            type,
            billingType,
            benefits,
            price,
            id: planId,
            trialDetails,
          },
        }),
      );
    }
  };

  const displayBenefits = isComingSoon ? ["Availability coming soon"] : (benefits ?? []);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        zIndex: isPopular ? 2 : 1,
        mt: isPopular && !isCurrentPlan ? { md: -2.5 } : 0,
        transform: isPopular && !isCurrentPlan ? { xs: "none", md: "scale(1.08)" } : "scale(1)",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
          transform: isPopular && !isCurrentPlan ? { xs: "translateY(-8px)", md: "scale(1.1) translateY(-10px)" } : "translateY(-8px)",
        },
      }}
    >
      {/* {isCurrentPlan && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: -2, zIndex: 3 }}>
          <Chip
            icon={<Icons.CheckCircle color="#FFF" />}
            label="Current Plan"
            sx={{
              bgcolor: currentPlanColor,
              color: "#FFF",
              fontWeight: 700,
              height: 34,
              p: 1,
              fontSize: "0.8rem",
              boxShadow: `0 8px 20px ${alphaVar(currentPlanColor, 0.4)}`,
            }}
          />
        </Box>
      )} */}

      {!isCurrentPlan && isPopular && !isComingSoon && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: -2, zIndex: 3 }}>
          <Chip
            icon={<Icons.Star color="#FFF" />}
            label="Most Popular"
            sx={{
              bgcolor: bannerColor,
              color: "#FFF",
              fontWeight: 700,
              height: 34,
              p: 1,
              fontSize: "0.8rem",
              boxShadow: `0 8px 20px ${alphaVar(bannerColor, 0.4)}`,
            }}
          />
        </Box>
      )}

      <Card
        onClick={onPreview}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          backgroundColor: isCurrentPlan ? alphaVar(currentPlanColor, 0.03) : "var(--fx-surface-base, #FFFFFF)",
          border: `1px solid ${isSelected ? colors.orange.base : isCurrentPlan ? currentPlanColor : isPopular ? bannerColor : "none"}`,
          boxShadow: isPopular ? `0 20px 40px ${alphaVar(bannerColor, 0.2)}` : "none",
          position: "relative",
          overflow: "visible",
          "&:hover": { cursor: "pointer" },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "left", mb: 2 }}>
          <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color: textPrimary, mb: description ? 1 : 0 }}>{title}</Typography>
          {description && <Typography sx={{ fontSize: "0.85rem", color: colors.gray[400], lineHeight: 1.4 }}>{description}</Typography>}
        </Box>

        {/* Precio y Trial Details */}
        <Box sx={{ mb: 3, pb: 2, borderBottom: `1px solid ${alphaVar(textPrimary, 0.08)}`, textAlign: "left" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "baseline" }}>
            <Typography
              sx={{
                fontSize: isPopular && !isCurrentPlan ? "2.8rem" : "2.4rem",
                fontWeight: 900,
                color: isCurrentPlan ? currentPlanColor : isPopular ? bannerColor : textPrimary,
              }}
            >
              ${formatPrice(price?.amount)}
            </Typography>

            <Typography sx={{ fontSize: "0.9rem", color: colors.gray[400], fontWeight: 600 }}>
              {billingType === "PAY_AS_YOU_GO" ? `Base price / ${frequency || "month"} + usage` : `/${frequency || "month"}`}
            </Typography>
          </Box>

          <Box sx={{ minHeight: "30px", mt: 1 }}>
            {trialDetails ? (
              <Typography
                sx={{
                  color: accentColor,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: alphaVar(accentColor, 0.08),
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  display: "inline-block",
                }}
              >
                Trial: ${formatPrice(trialDetails?.pricing_scheme?.fixed_price.value)} for {trialDetails?.frequency?.interval_count}{" "}
                {trialDetails?.frequency?.interval_unit?.toLowerCase()}s
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* Monthly Usage Rates for PAY_AS_YOU_GO */}
        {billingType === "PAY_AS_YOU_GO" && (
          <Box sx={{ mb: 3, textAlign: "left" }}>
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                color: colors.gray[500],
                mb: 1,
              }}
            >
              Usage Rates
            </Typography>
            {loadingUsagePricing ? (
              <Typography sx={{ fontSize: "0.8rem", color: colors.gray[400] }}>Loading usage rates...</Typography>
            ) : usagePricing.length > 0 ? (
              <Stack spacing={0.75}>
                {usagePricing.map((item) => (
                  <Box
                    key={item.uid || item.code}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: "0.82rem",
                      bgcolor: alphaVar(colors.orange.base, 0.04),
                      px: 1.25,
                      py: 0.6,
                      borderRadius: 1.5,
                      border: `1px solid ${alphaVar(colors.orange.base, 0.12)}`,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.gray.base }}>{item.description || item.code}</Typography>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 800, color: colors.orange.base }}>
                      ${Number(item.unitPrice).toFixed(4)} / {item.unitType}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : null}
          </Box>
        )}

        {/* Beneficios */}
        {/*  <Box sx={{ flexGrow: 1, mb: 4 }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, textTransform: "uppercase", color: textPrimary, mb: 2, letterSpacing: 1 }}>
            {isFreePlan ? "What's Included" : "Everything in previous plan plus:"}
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, columnGap: 2 }}>
            {displayBenefits.map((benefit, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "flex-start", minWidth: 0 }}>
                <Box sx={{ mt: 0.3, flexShrink: 0 }}>
                  <Icons.Check color={isComingSoon ? alphaVar(accentColor, 0.3) : accentColor} />
                </Box>
                <Typography
                  title={benefit}
                  sx={{
                    fontSize: "0.85rem",
                    color: colors.gray[500],
                    textAlign: "left",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                >
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box> */}

        {/* Botón */}
        <Button
          onClick={(e) => {
            e.stopPropagation();
            handleChoosePlan();
          }}
          fullWidth
          disabled={isComingSoon}
          sx={{
            py: isPopular && !isCurrentPlan ? 1.8 : 1.5,
            borderRadius: 2,
            bgcolor: isCurrentPlan ? alphaVar(currentPlanColor, 0.08) : isPopular ? bannerColor : "transparent",
            color: isCurrentPlan ? currentPlanColor : isPopular ? "#FFF" : bannerColor,
            border: `2px solid ${accentColor}`,
            fontWeight: 800,
            fontSize: "1rem",
            textTransform: "none",
            cursor: isCurrentPlan ? "default" : "pointer",
            boxShadow: isPopular && !isCurrentPlan ? `0 8px 20px ${alphaVar(bannerColor, 0.3)}` : "none",
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: isCurrentPlan ? alphaVar(currentPlanColor, 0.08) : isPopular ? colors.orange[600] : alphaVar(bannerColor, 0.05),
              boxShadow: isPopular && !isCurrentPlan ? `0 12px 28px ${alphaVar(bannerColor, 0.45)}` : "none",
              transform: isCurrentPlan ? "none" : "scale(1.02)",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                animation: isPopular && !isCurrentPlan ? "pulse-ring 1.5s infinite" : "none",
              },
            },
            "@keyframes pulse-ring": {
              "0%": { transform: "scale(1)", opacity: 0.8 },
              "100%": { transform: "scale(1.4)", opacity: 0 },
            },
            "&.Mui-disabled": { opacity: 0.5, bgcolor: alphaVar(bannerColor, 0.2) },
          }}
        >
          {isCurrentPlan ? "Current Plan" : isComingSoon ? comingSoonLabel || "Soon" : "Choose Plan"}
          {isCurrentPlan && <Icons.CheckCircle color={currentPlanColor} />}
          {!isCurrentPlan && !isComingSoon && <Icons.ArrowRight color={isPopular ? "#FFF" : bannerColor} />}
        </Button>
      </Card>

      {!isComingSoon && contactUs && <BusinessContact bannerHexColor={bannerColor} setContactUs={setContactUs} />}
    </Box>
  );
};

export const PlanBenefitsPanel = ({
  title,
  description,
  benefits = [],
  price,
  isCurrentPlan,
  isFreePlan,
  previousPlanTitle,
  newBenefits = [],
  onChoose,
}) => {
  const benefitsToHighlight = isFreePlan ? benefits : newBenefits;
  const hasNewBenefits = benefitsToHighlight.length > 0;

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: "none",
        backgroundColor: "var(--fx-surface-base, #FFFFFF)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          p: 2.5,
          borderBottom: `1px solid ${alphaVar(colors.gray[400], 0.14)}`,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: 2,
            bgcolor: alphaVar(colors.orange.base, 0.08),
            color: colors.orange.base,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icons.CheckCircle color={colors.orange.base} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "1.2rem",
              fontWeight: 900,
              color: colors.gray.base,
            }}
          >
            Benefits details for {title}
          </Typography>

          {description && (
            <Typography
              sx={{
                fontSize: "0.85rem",
                color: colors.gray[400],
                fontWeight: 600,
                mt: 0.25,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: colors.orange.base,
            mb: 0.75,
          }}
        >
          {isFreePlan ? "What's included" : previousPlanTitle ? `Compared to ${previousPlanTitle}, ${title} adds:` : "Included benefits"}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.88rem",
            color: colors.gray[400],
            fontWeight: 600,
            mb: 3,
            maxWidth: 720,
            lineHeight: 1.45,
          }}
        >
          {isFreePlan
            ? "Start with the essential tools included in your free plan."
            : previousPlanTitle
              ? `See what ${title} unlocks beyond ${previousPlanTitle}.`
              : `${title} includes the following benefits.`}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            gap: 3,
          }}
        >
          {(hasNewBenefits ? benefitsToHighlight : benefits).map((benefit, index) => (
            <Box
              key={`${title}-benefit-${index}`}
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
              }}
            >
              <Box display={"flex"} alignItems={"center"} justifyContent={"center"} sx={{ gap: 1.5 }}>
                <FiberManualRecord
                  sx={{
                    color: colors.orange.base,
                    fontSize: 10,
                  }}
                />

                <Box>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "0.95rem",
                      color: colors.gray.base,
                      lineHeight: 1,
                    }}
                  >
                    {typeof benefit === "string" ? benefit : benefit?.name}
                  </Typography>

                  {typeof benefit !== "string" && benefit?.description && (
                    <Typography
                      sx={{
                        fontSize: "0.82rem",
                        color: colors.gray[400],
                        mt: 0.3,
                        lineHeight: 1.35,
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
};
